'use client';

import {
  Box,
  Button,
  Divider,
  Drawer,
  Fab,
  FormControlLabel,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { useStatsigClient } from '@statsig/react-bindings';
import axios from 'axios';
import type React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { post } from '../../api/axios';
import { payOrder } from '../../api/order';
import { completeOrder } from '../../api/order/[id]/complete';
import { OrderApi } from '../../api/order/order.api';
import useActiveExperimentsQuery from '../../api/statsig/hooks/useActiveExperimentsQuery';
import useGatesQuery from '../../api/statsig/hooks/useGatesQuery';
import { deleteMySubscriptions } from '../../api/subscription';
import { deleteMyAccount, LogInMethod, login } from '../../api/user';
import SpaceY from '../../components/SpaceY';
import { orderHistoryPageRegex } from '../../constants/regex';
import {
  StatsigUserSyncStatus,
  useStatsigUserSyncStore,
} from '../../store/statsigUserSyncStore';
import { useCustomizingBackModalGuardStore } from '../../store/useCustomizingBackModalGuardStore';
import { OrderStatus } from '../../type';
import { isLocalDevelopment, isStageDev } from '../../utils/common';
import { useIdParam } from '../../utils/hook/hook';
import useOnLogout from '../../utils/hook/login/useOnLogout';
import { useQaForcePaymentFailHeader } from '../../utils/hook/payment/useQaForcePaymentFailHeader';
import { useBottomNavigation } from '../../utils/hook/react-native';
import { useOrderDetailQuery } from '../../utils/hook/react-query/mypages/useOrderDetailQuery';
import useMeQuery from '../../utils/hook/react-query/user/useMeQuery';
import { usePathnameMethod } from '../../utils/hook/usePathnameMethod';
import { useOnLoginSuccess } from '../../utils/hook/users';
import useStatsigExperimentOverride from '../../utils/hook/useStatsigExperimentOverride';
import { MockedTimeManager } from '../../utils/MockedTimeManager';
import StatsigExperimentOverrideSection from './devtool/StatsigExperimentOverrideSection';

const USER_SYNC_STATUS_COLOR: Record<StatsigUserSyncStatus, string> = {
  [StatsigUserSyncStatus.IDLE]: 'bg-red-400',
  [StatsigUserSyncStatus.PENDING]: 'bg-yellow-500',
  [StatsigUserSyncStatus.SYNCED]: 'bg-green-500',
  [StatsigUserSyncStatus.NO_USER]: 'bg-gray-500',
};

const USER_SYNC_STATUS_HINT: Record<StatsigUserSyncStatus, string> = {
  [StatsigUserSyncStatus.IDLE]:
    '초기 상태 — authenticatedOnly=true 실험은 대기, false는 즉시 평가',
  [StatsigUserSyncStatus.PENDING]:
    'me 로딩 중 또는 updateUserAsync 진행 중 — authenticatedOnly=true 실험은 대기',
  [StatsigUserSyncStatus.SYNCED]: 'userID 주입 완료 — 실험 exposure 정상 기록',
  [StatsigUserSyncStatus.NO_USER]:
    '미로그인 또는 동기화 실패 — authenticatedOnly=true 실험은 control 고정 + exposure 미기록',
};

const LayoutUglyusDevTool = () => {
  const { me } = useMeQuery();
  const orderId = useIdParam();
  const { addBottomHeightRN } = useBottomNavigation();
  const { pathname } = usePathnameMethod();
  const mockedTimeManager = useMemo(() => MockedTimeManager.getInstance(), []);
  const { onLogout } = useOnLogout();
  const onLoginSuccess = useOnLoginSuccess();

  // 정확히 /mypages/order-history/[id] 경로만 매칭
  const isOrderHistoryDetailPage = orderHistoryPageRegex.test(pathname);

  const {
    data: order,
    isLoading: isOrderLoading,
    refetch: refetchOrder,
  } = useOrderDetailQuery(orderId, {
    enabled: !!orderId && isOrderHistoryDetailPage,
  });

  const isCustomizingBackModalGuardDisabled =
    useCustomizingBackModalGuardStore.use.isGuardDisabled();
  const setIsCustomizingBackModalGuardDisabled =
    useCustomizingBackModalGuardStore.use.setIsGuardDisabled();
  const { client, isLoading: isStatsigLoading } = useStatsigClient();
  const userSyncStatus = useStatsigUserSyncStore.use.status();
  const { getOverriddenVariant } = useStatsigExperimentOverride();

  const { data: activeExperiments = [] } = useActiveExperimentsQuery();
  const statsigVariants = useMemo(() => {
    if (isStatsigLoading || activeExperiments.length === 0) return {};
    return Object.fromEntries(
      activeExperiments.map((exp) => [
        exp.id,
        {
          variant: client.getExperiment(exp.id).get('variant', null) as
            | number
            | null,
          status: exp.status,
        },
      ]),
    );
  }, [client, isStatsigLoading, activeExperiments]);

  const { data: gates = [] } = useGatesQuery();
  const statsigGates = useMemo(() => {
    if (isStatsigLoading || gates.length === 0) return [];
    return gates.map((gate) => ({
      id: gate.id,
      name: gate.name,
      status: gate.status,
      isEnabled: gate.isEnabled,
      isPass: client.checkGate(gate.id),
    }));
  }, [client, isStatsigLoading, gates]);

  // Statsig stableID — 비로그인 사용자 버킷팅 단위. QA 시 Statsig Overrides에 등록하여 그룹 강제 진입 가능
  const statsigStableId = useMemo(() => {
    if (isStatsigLoading) return null;
    return client.getContext().stableID;
  }, [client, isStatsigLoading]);

  const {
    isForcePaymentFailEnabled,
    setIsForcePaymentFailEnabled,
    qaPaymentFailRequestOptions,
  } = useQaForcePaymentFailHeader();

  const [open, setOpen] = useState(false);
  const [mockTimeEnabled, setMockTimeEnabled] = useState(false);
  const [mockTimeValue, setMockTimeValue] = useState('');
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [experimentSearch, setExperimentSearch] = useState('');
  const [gateSearch, setGateSearch] = useState('');
  const [loginMessage, setLoginMessage] = useState('');

  // 컴포넌트 마운트 시 세션 스토리지에서 모킹 설정 불러오기
  useEffect(() => {
    if (typeof window !== 'undefined') {
      const storedMockTimeEnabled = mockedTimeManager.isEnabled();
      const storedMockTimeValue = mockedTimeManager.getMockedTimeValue();

      setMockTimeEnabled(storedMockTimeEnabled);

      if (storedMockTimeValue) {
        setMockTimeValue(storedMockTimeValue);
      } else {
        const now = new Date();
        setMockTimeValue(now.toISOString().slice(0, 16)); // YYYY-MM-DDTHH:MM 형식
      }
    }
  }, [mockedTimeManager]);

  // 모킹 활성화 상태 변경 핸들러
  const handleMockTimeEnabledChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newEnabled = e.target.checked;
    setMockTimeEnabled(newEnabled);
    mockedTimeManager.setEnabled(newEnabled);

    if (newEnabled && mockTimeValue) {
      mockedTimeManager.setMockedTime(mockTimeValue, true);
    }
  };

  // 모킹 시간 값 변경 핸들러
  const handleMockTimeValueChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newValue = e.target.value;
    setMockTimeValue(newValue);

    if (mockTimeEnabled && newValue) {
      mockedTimeManager.setMockedTime(newValue, true);
    }
  };

  // [QA] 회원 탈퇴 — 구독·계정 삭제 후 로그아웃. 신규 가입 funnel 반복 테스트용.
  // confirm 단계로 오삭제를 막고, 구독 제거는 best-effort(구독 없는 계정 대비)로 처리한다.
  // 구출패스 환불은 생략하므로, 활성 구출패스가 있으면 마이페이지 > 회원탈퇴 플로우를 이용한다.
  const handleQaWithdraw = () => {
    const isConfirmed = window.confirm(
      '정말 회원 탈퇴하시겠습니까?\n구독·계정이 모두 삭제되고 로그아웃됩니다. (QA 전용, 복구 불가)',
    );
    if (!isConfirmed) return;

    deleteMySubscriptions()
      .catch(() => undefined)
      .then(() => deleteMyAccount({ deletedReason: 'QA 테스트 탈퇴' }))
      // then(성공, 실패) 2-인자: 성공 콜백(onLogout 등) 내부 예외를 "탈퇴 실패"로 오인하지 않도록 deleteMyAccount 실패만 처리
      .then(
        () => {
          window.alert('회원 탈퇴 완료. 로그아웃합니다.');
          onLogout();
        },
        (error: unknown) => {
          const message = axios.isAxiosError(error)
            ? error.response?.data?.message
            : undefined;
          window.alert(`회원 탈퇴 실패: ${message ?? '알 수 없는 오류'}`);
        },
      );
  };

  // [QA] 클라이언트 저장값 전체 초기화 — 인앱 리뷰 재노출 이력·로그인·모킹 등을 한 번에 비운다.
  // 전체 비우기는 키 단위 usehooks-ts로 불가능하므로 DevTool 한정으로 직접 clear한다.
  // httpOnly 쿠키(서버 세션)는 JS에서 만료시킬 수 없어 남을 수 있다.
  const handleClearClientStorage = () => {
    const isConfirmed = window.confirm(
      '쿠키·로컬스토리지·세션스토리지를 모두 초기화합니다.\n로그인·시간 모킹·인앱 리뷰 노출 이력 등이 전부 삭제되고 새로고침됩니다. (QA 전용)',
    );
    if (!isConfirmed) return;

    window.localStorage.clear();
    window.sessionStorage.clear();

    for (const rawCookie of document.cookie.split(';')) {
      const cookieName = rawCookie.split('=')[0].trim();
      if (cookieName) {
        document.cookie = `${cookieName}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
      }
    }

    window.location.reload();
  };

  return (
    <>
      <Fab
        onClick={() => setOpen(true)}
        size="large"
        color="default"
        sx={{
          position: 'fixed',
          left: '16px',
          height: '60px',
          width: '60px',
          backgroundColor: 'rgba(107, 114, 128, 0.5)',
          bottom: addBottomHeightRN(16),
        }}
      >
        <p className="text-30px text-black/50">⚙️</p>
      </Fab>
      <Drawer
        anchor="left"
        open={open}
        onClose={() => setOpen(false)}
        sx={{ zIndex: 9999 }}
      >
        <Box className="min-w-[90vw] max-w-[90vw] sm:min-w-[33vw] sm:max-w-[33vw]">
          <Box className="p-16px">
            <Typography className="animate-pulse text-20px font-bold">
              Uglyus Devleoper Tools
            </Typography>
          </Box>

          <Divider />
          <Box className="p-16px">
            <Typography className="mb-8px text-16px font-bold">
              인증 (
              {me ? (
                <button
                  className="underline"
                  onClick={() => {
                    navigator.clipboard
                      .writeText(me.email ?? me.id.toString())
                      .then(() => {
                        if (typeof window !== 'undefined') {
                          window.alert('복사되었습니다.');
                        }
                      });
                  }}
                  type="button"
                >
                  {me.email ?? me.id}
                </button>
              ) : (
                '미로그인'
              )}
              )
            </Typography>

            {me ? (
              <Box className="flex flex-col gap-8px">
                <Button
                  variant="contained"
                  color="error"
                  size="small"
                  fullWidth
                  onClick={onLogout}
                >
                  로그아웃
                </Button>
                <Button
                  variant="outlined"
                  color="error"
                  size="small"
                  fullWidth
                  onClick={handleQaWithdraw}
                >
                  회원 탈퇴 (QA)
                </Button>
              </Box>
            ) : (
              <Box className="flex flex-col gap-8px">
                <TextField
                  label="이메일"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  size="small"
                  fullWidth
                />
                <TextField
                  label="비밀번호"
                  type="password"
                  value={loginPassword}
                  onChange={(e) => setLoginPassword(e.target.value)}
                  size="small"
                  fullWidth
                />
                {loginMessage && (
                  <Typography className="text-12px text-red-500">
                    {loginMessage}
                  </Typography>
                )}
                <Button
                  variant="contained"
                  size="small"
                  fullWidth
                  disabled={!loginEmail || !loginPassword}
                  onClick={() => {
                    setLoginMessage('');
                    login({
                      email: loginEmail,
                      password: loginPassword,
                      logInMethod: LogInMethod.LOCAL,
                    })
                      .then(async (response) => {
                        await onLoginSuccess(response.data.token);
                        window.location.reload();
                      })
                      .catch((e) =>
                        setLoginMessage(
                          e?.response?.data?.message ?? '로그인 실패',
                        ),
                      );
                  }}
                >
                  이메일 로그인
                </Button>
              </Box>
            )}
          </Box>

          <Divider />
          <Box className="p-16px">
            <Typography className="mb-8px text-16px font-bold">
              스토리지 초기화
            </Typography>
            <Button
              variant="outlined"
              color="error"
              size="small"
              fullWidth
              onClick={handleClearClientStorage}
            >
              쿠키·로컬·세션 스토리지 전체 삭제 후 새로고침
            </Button>
            <Typography className="mt-8px text-14px text-gray-600">
              로그인·시간 모킹·인앱 리뷰 노출 이력 등 모든 클라이언트 저장값을
              비웁니다. httpOnly 쿠키(서버 세션)는 JS로 지울 수 없어 남을 수
              있습니다.
            </Typography>
          </Box>

          {typeof window !== 'undefined' && (
            <>
              <Divider />
              <Box className="p-16px">
                <Typography className="text-16px font-bold">
                  현재 URL
                </Typography>
                <Typography className="break-all text-14px">
                  {window.location.href}
                </Typography>
              </Box>
            </>
          )}

          <Divider />
          <Box className="p-16px">
            <Typography className="mb-8px text-16px font-bold">
              커마 뒤로가기 가드
            </Typography>
            <Box className="mb-8px flex items-center">
              <FormControlLabel
                control={
                  <Switch
                    checked={isCustomizingBackModalGuardDisabled}
                    onChange={(e) => {
                      setIsCustomizingBackModalGuardDisabled(e.target.checked);
                    }}
                  />
                }
                label="뒤로가기 가드 비활성화"
              />
            </Box>
            <Typography className="text-14px text-gray-600">
              비활성화 시 커마 페이지에서 뒤로가기/탭 닫기 시 확인 모달 없이
              이동
            </Typography>
          </Box>

          <Divider />
          <Box className="p-16px">
            <Typography className="mb-8px text-16px font-bold">
              시간 모킹
            </Typography>

            <Box className="mb-8px flex items-center">
              <FormControlLabel
                control={
                  <Switch
                    checked={mockTimeEnabled}
                    onChange={handleMockTimeEnabledChange}
                  />
                }
                label="시간 모킹 활성화"
              />
            </Box>

            <Box className="mb-16px flex flex-col gap-8px">
              <TextField
                label="모킹 시간 설정"
                type="datetime-local"
                value={mockTimeValue}
                onChange={handleMockTimeValueChange}
                disabled={!mockTimeEnabled}
                InputLabelProps={{
                  shrink: true,
                }}
                fullWidth
                size="small"
              />
              <Typography className="text-14px">
                현재 시간: {mockedTimeManager.getMockedTimeString()}
              </Typography>
            </Box>
          </Box>

          {isStageDev && (
            <>
              <Divider />
              <Box className="p-16px">
                <Typography className="mb-8px text-16px font-bold">
                  결제 강제 실패 (커마 QA)
                </Typography>
                <Box className="mb-8px flex items-center">
                  <FormControlLabel
                    control={
                      <Switch
                        checked={isForcePaymentFailEnabled}
                        onChange={(e) =>
                          setIsForcePaymentFailEnabled(e.target.checked)
                        }
                      />
                    }
                    label="다음 커마/재결제 강제 실패"
                  />
                </Box>
                <Typography className="text-14px text-gray-600">
                  ON이면 다음 커마 결제·재결제가 토스 테스트코드로 실제
                  실패합니다 (stage 전용).
                </Typography>
              </Box>
            </>
          )}

          {isOrderHistoryDetailPage && orderId && (
            <>
              <Divider />
              <Box className="p-16px">
                <Typography className="mb-8px text-16px font-bold">
                  오더 상태 조회
                </Typography>
                {isOrderLoading ? (
                  <Typography className="text-14px text-gray-600">
                    로딩 중...
                  </Typography>
                ) : order ? (
                  <Box className="flex flex-col gap-8px rounded-[8px] border border-gray-200 p-16px">
                    <Box className="flex items-center justify-between">
                      <Typography className="text-14px font-semibold">
                        오더 ID:
                      </Typography>
                      <Typography className="text-14px">{order.id}</Typography>
                    </Box>
                    <Box className="flex items-center justify-between">
                      <Typography className="text-14px font-semibold">
                        상태:
                      </Typography>
                      <Typography className="text-14px font-bold text-brand-1">
                        {order.status}
                      </Typography>
                    </Box>
                    <Box className="flex items-center justify-between">
                      <Typography className="text-14px font-semibold">
                        결제 상태:
                      </Typography>
                      <Typography className="text-14px">
                        {order.paymentStatus || 'SUCCESS'}
                      </Typography>
                    </Box>
                    <Box className="flex items-center justify-between">
                      <Typography className="text-14px font-semibold">
                        배송일:
                      </Typography>
                      <Typography className="text-14px">
                        {order.deliveryDate
                          ? new Date(order.deliveryDate).toLocaleDateString(
                              'ko-KR',
                            )
                          : '-'}
                      </Typography>
                    </Box>
                    <Box className="flex items-center justify-between">
                      <Typography className="text-14px font-semibold">
                        시퀀스:
                      </Typography>
                      <Typography className="text-14px">
                        {order.sequence}회차
                      </Typography>
                    </Box>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() => refetchOrder()}
                      className="mt-8px"
                    >
                      상태 새로고침
                    </Button>
                  </Box>
                ) : (
                  <Typography className="text-14px text-gray-600">
                    오더 정보를 불러올 수 없습니다.
                  </Typography>
                )}
              </Box>

              {order?.status === OrderStatus.PICKING && (
                <>
                  <Divider />
                  <Button
                    onClick={() =>
                      payOrder(orderId, qaPaymentFailRequestOptions)
                        .then(() => window.location.reload())
                        .catch((e) => alert(e?.response?.data?.message))
                    }
                  >
                    오더 id: {orderId} 결제 (1,2 차 자동 결제와 동일)
                  </Button>
                </>
              )}

              {order?.status === OrderStatus.READY && (
                <>
                  <Divider />
                  <Button
                    onClick={() =>
                      OrderApi.startDeliveringPrepare(orderId)
                        .then(() => window.location.reload())
                        .catch((e) => alert(e?.response?.data?.message))
                    }
                  >
                    READY -&gt; DELIVERING_PREPARE 상태 변경
                  </Button>
                </>
              )}

              {order?.status === OrderStatus.DELIVERING_PREPARE && (
                <>
                  <Divider />
                  <Button
                    onClick={() =>
                      post(`/order/${orderId}/delivering`, {})
                        .then(() => window.location.reload())
                        .catch((e) => alert(e?.response?.data?.message))
                    }
                  >
                    DELIVERING_PREPARE -&gt; DELIVERING 상태 변경
                  </Button>
                </>
              )}

              {order?.status === OrderStatus.DELIVERING && (
                <>
                  <Divider />
                  <Button
                    onClick={() =>
                      completeOrder(orderId)
                        .then(() => window.location.reload())
                        .catch((e) => alert(e?.response?.data?.message))
                    }
                  >
                    DELIVERING -&gt; COMPLETE 상태 변경 (도착 확정)
                  </Button>
                </>
              )}
            </>
          )}

          <Divider />

          {isLocalDevelopment && (
            <>
              <StatsigExperimentOverrideSection />

              <Divider />
            </>
          )}

          <Box className="p-16px ">
            <Typography
              className="text-16px font-bold"
              style={{ marginBottom: '8px' }}
            >
              Statsig 실험 확인 (User Id:{' '}
              {!!me ? (
                <button
                  className="underline"
                  onClick={() => {
                    navigator.clipboard
                      .writeText(me?.id.toString() ?? '')
                      .then(() => {
                        if (typeof window !== 'undefined') {
                          window.alert('복사되었습니다.');
                        }
                      });
                  }}
                  type="button"
                >
                  {me?.id}
                </button>
              ) : (
                '미로그인 상태'
              )}
              )
            </Typography>

            <Box className="flex flex-col gap-4px rounded-[8px] border border-gray-200 p-12px">
              <Box className="flex items-center gap-8px">
                <Typography className="text-14px font-semibold">
                  User Sync:
                </Typography>
                <span
                  className={`inline-block rounded-full px-[8px] py-[2px] text-[11px] font-bold text-white ${USER_SYNC_STATUS_COLOR[userSyncStatus]}`}
                >
                  {userSyncStatus}
                </span>
              </Box>
              <Typography className="text-[11px] text-gray-600">
                {USER_SYNC_STATUS_HINT[userSyncStatus]}
              </Typography>
              <Box className="flex items-center gap-8px">
                <Typography className="shrink-0 text-14px font-semibold">
                  Stable ID:
                </Typography>
                {statsigStableId ? (
                  <button
                    className="break-all text-left text-14px underline"
                    onClick={() => {
                      navigator.clipboard
                        .writeText(statsigStableId)
                        .then(() => {
                          if (typeof window !== 'undefined') {
                            window.alert('복사되었습니다.');
                          }
                        });
                    }}
                    type="button"
                  >
                    {statsigStableId}
                  </button>
                ) : (
                  <Typography className="text-14px text-gray-500">
                    (로딩 중)
                  </Typography>
                )}
              </Box>
            </Box>

            <SpaceY height={8} />
            <TextField
              placeholder="실험 ID 검색 (예: eid413)"
              value={experimentSearch}
              onChange={(e) => setExperimentSearch(e.target.value)}
              size="small"
              fullWidth
            />
            <SpaceY height={8} />
            <ul className="flex flex-col gap-8px rounded-[8px] border border-gray-200 p-16px">
              {Object.entries(statsigVariants)
                .filter(([experimentName]) =>
                  experimentName
                    .toLowerCase()
                    .includes(experimentSearch.toLowerCase()),
                )
                .map(([experimentName, { variant, status }]) => (
                  <div
                    key={experimentName}
                    className="flex items-center gap-4px"
                  >
                    <span
                      className={`inline-block rounded-full px-[6px] py-[1px] text-[10px] font-bold text-white ${status === 'active' ? 'bg-green-500' : 'bg-yellow-500'}`}
                    >
                      {status}
                    </span>
                    {/* 강제 배정 중이면 아래 실제 배정값 대신 그 실험안이 화면에 나온다 */}
                    {getOverriddenVariant(experimentName) !== null && (
                      <span className="inline-block rounded-full bg-purple-500 px-[6px] py-[1px] text-[10px] font-bold text-white">
                        강제 배정 중
                      </span>
                    )}
                    <p>
                      🧪 {experimentName.toUpperCase()} -{' '}
                      {variant === null
                        ? '미설정'
                        : variant === 0
                          ? '기존'
                          : String.fromCharCode(
                              96 + variant,
                            ).toUpperCase()}{' '}
                      안
                    </p>
                  </div>
                ))}
            </ul>
          </Box>

          <Divider />

          <Box className="p-16px">
            <Typography
              className="text-16px font-bold"
              style={{ marginBottom: '8px' }}
            >
              Statsig Feature Gate 확인
            </Typography>
            <Typography className="mb-8px text-12px text-gray-600">
              현재 사용자 기준 pass/fail 평가 결과. 콘솔에서 룰을 변경하면 5분
              내 반영됩니다.
            </Typography>
            <TextField
              placeholder="gate ID 검색"
              value={gateSearch}
              onChange={(e) => setGateSearch(e.target.value)}
              size="small"
              fullWidth
            />
            <SpaceY height={8} />
            <ul className="flex flex-col gap-8px rounded-[8px] border border-gray-200 p-16px">
              {statsigGates.length === 0 ? (
                <Typography className="text-12px text-gray-600">
                  로딩 중이거나 gate가 없습니다.
                </Typography>
              ) : (
                statsigGates
                  .filter((gate) =>
                    gate.id.toLowerCase().includes(gateSearch.toLowerCase()),
                  )
                  .map((gate) => (
                    <div key={gate.id} className="flex flex-col gap-2px">
                      <div className="flex flex-wrap items-center gap-4px">
                        <span
                          className={`inline-block rounded-full px-[6px] py-[1px] text-[10px] font-bold text-white ${gate.isPass ? 'bg-green-500' : 'bg-gray-400'}`}
                        >
                          {gate.isPass ? 'PASS' : 'FAIL'}
                        </span>
                        <span
                          className={`inline-block rounded-full px-[6px] py-[1px] text-[10px] font-bold text-white ${gate.isEnabled ? 'bg-blue-500' : 'bg-red-400'}`}
                        >
                          {gate.isEnabled ? 'ENABLED' : 'DISABLED'}
                        </span>
                        <p className="text-14px">🚪 {gate.id}</p>
                      </div>
                      <p className="pl-[8px] text-[11px] text-gray-600">
                        {gate.name} · {gate.status}
                      </p>
                    </div>
                  ))
              )}
            </ul>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};

export default LayoutUglyusDevTool;
